import React, { Component } from 'react';
import Head from 'next/head';
import MarkDown from 'react-markdown';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import {
  getCompanyRegistrationOffice,
  getCompanyCountryAdjective,
  FEF_HOST,
  getCompanyCountryByHost,
  getCompanyNameByHost,
  getCompanyNumber,
  getLandingAddress,
  getPrivacyPolicyslug,
  getRoute,
  isBlogDomain,
} from '/lib/helpers';
import { replaceString } from '/imports/core/ui/helpers';
import { withDomain } from '/imports/core/hooks/useDomain';
import { withIntl } from '/imports/core/api/useIntl';
import { withResponsiveContext } from '/imports/core/api/responsiveContext';
import { withTracking } from '/imports/core/hooks/useTracking';
import { getSiteFromDomain } from 'server/utils';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const getPolicy = (t, locale, host, getBrandName, getContactEmail) => {
  const getString = () => {
    const value = getContactEmail();
    let replacedValue = {};
    if (value?.value) {
      replacedValue = {
        TITLE: value.title,
        'contact@resumedone.io': value.value,
      };
    } else {
      replacedValue = {
        TITLE: value,
        'contact@resumedone.io': value,
      };
    }

    return replaceString(t('pp_que8_para2'), replacedValue);
  };

  const getContent = () => {
    //temporary
    if (host === 'criarcv') {
      return t('pp_que10_para10_new');
    }
    let replacedValue = {};
    replacedValue = {
      COMPANY_NAME: getCompanyNameByHost(host),
      COMPANY_COUNTRY: getCompanyCountryByHost(host),
      LANDING_ADDRESS: getLandingAddress(host),
    };
    return replaceString(t('pp_que10_para10'), replacedValue);
  };

  const getPolicyTitleText = (host) => {
    let replacedValue = {
      Resumedone: getBrandName(),
    };
    if (FEF_HOST.includes(host)) {
      replacedValue = {
        ...replacedValue,
        '8 B RUE MAGNE 91150 ETAMPES': getLandingAddress(host),
      };
    }
    return replaceString(t(getPrivacyPolicyslug(host)), replacedValue);
  };

  return `
# ${t('footer.privacy_policy').toLocaleUpperCase(locale)}

${getPolicyTitleText(host)}

${replaceString(t('pp_desc2'), {
  ['/terms-and-conditions']: getRoute('/terms-and-conditions', locale, false, host, isBlogDomain(host)),
})}

${t('pp_desc3')}

## 1. ${t('pp_que1')}

${t('pp_que1_para1')}

#### ${t('pp_que1_para2')}
* ${t('pp_que1_para3')}

* ${t('pp_que1_para4')}

* ${t('pp_que1_para5')}

* ${t('pp_que1_para6')} 

* ${t('pp_que1_para7')}

## 2. ${t('pp_que2')}

${t('pp_que2_para1')}

${t('pp_que2_para2')}

${t('pp_que2_para3')}

## 3. ${t('pp_que3')}

#### ${t('pp_que3_title')}

* ${t('pp_que3_para1')}

* ${t('pp_que3_para2')}

* ${t('pp_que3_para3')}

* ${t('pp_que3_para4')}

* ${t('pp_que3_para5')}

* ${t('pp_que3_para6')}

* ${t('pp_que3_para7')}

* ${t('pp_que3_para8')}

* ${t('pp_que3_para9')}

## 4. ${t('pp_que4')}

${t('pp_que4_desc')}

${t('pp_que4_title')}

* ${t('pp_que4_para1')}

* ${t('pp_que4_para2')}

* ${t('pp_que4_para3')}

* ${replaceString(t('pp_que4_para4'), { Resumedone: getBrandName() })}

## 5. ${t('pp_que5')}

${t('pp_que5_desc')}

* ${t('pp_que5_para1')}

* ${t('pp_que5_para2')}

* ${t('pp_que5_para3')}

* ${t('pp_que5_para4')}
## 6. ${t('pp_que6')}

${t('pp_que6_para1')}

## 7. ${t('pp_que7')}

${t('pp_que7_para1')}

## 8. ${t('pp_que8')}

${t('pp_que8_para1')}

${getString()}

## 9. ${t('pp_que9')}.

${t('pp_que9_para1')}


## 10. ${t('pp_que10')}

${getContent()}
`;
};

const newGetPolicy = (t, locale, host, getBrandName, getContactEmail) => {
  const getString = () => {
    const value = getContactEmail();
    let replacedValue = {};
    if (value?.value) {
      replacedValue = {
        email: value.title,
        'contact@resumedone.io': value.value,
      };
    } else {
      replacedValue = {
        email: value,
        'contact@resumedone.io': value,
      };
    }

    return replaceString(t('pp_new_que10_para1'), replacedValue);
  };

  const getPolicyTitleText = (host) => {
    let replacedValue = {
      Resumedone: getBrandName(),
    };
    if (FEF_HOST.includes(host)) {
      replacedValue = {
        ...replacedValue,
        '8 B RUE MAGNE 91150 ETAMPES': getLandingAddress(host),
      };
    }
    return replaceString(t(getPrivacyPolicyslug(host)), replacedValue);
  };

  return `
# ${t('footer.privacy_policy').toLocaleUpperCase(locale)}

#### ${t('pp_new_date')}

${getPolicyTitleText(host)}

${replaceString(t('pp_new_desc2'), {
  ResumeDone: getBrandName(),
})}

${t('pp_new_desc3')}

* ${replaceString(t('pp_new_desc3_para1'), {
    ['/website']: getSiteFromDomain(host),
  })}

* ${t('pp_new_desc3_para2')}

${t('pp_new_desc4')}

## 1. ${t('pp_new_que1')}

${replaceString(t('pp_new_que1_para1'), {
  Resumedone: getBrandName(),
  COMPANY_NAME: getCompanyNameByHost(host),
  REGISTRATION_NUMBER: getCompanyNumber(host),
  ADDRESS: getLandingAddress(host),
  COUNTRY_ADJECTIVE: getCompanyCountryAdjective(host),
  REGISTRATION_OFFICE: getCompanyRegistrationOffice(host),
})}

## 2. ${t('pp_new_que2')}

${t('pp_new_que2_para1')}

* ### ${t('pp_new_que2_para2')}

  * ${t('pp_new_que2_para3')}

  * ${t('pp_new_que2_para4')}

  * ${t('pp_new_que2_para5')}

  * ${t('pp_new_que2_para6')}

* ### ${t('pp_new_que2_para7')}

  * ${t('pp_new_que2_para8')}

  * ${t('pp_new_que2_para9')}

  * ${t('pp_new_que2_para10')}

* ### ${t('pp_new_que2_para11')}

  * ${t('pp_new_que2_para12')}

  * ${t('pp_new_que2_para13')}

${t('pp_new_que2_para14')}

${replaceString(t('pp_new_que2_para15'), { Resumedone: getBrandName() })}

## 3. ${t('pp_new_que3')}

${replaceString(t('pp_new_que3_para1'), { Resumedone: getBrandName() })}

${t('pp_new_que3_para2')}

* ### ${t('pp_new_que3_para3')}

  * ${t('pp_new_que3_para4')}

* ### ${t('pp_new_que3_para5')}
  
  * ${t('pp_new_que3_para6')}

  * ${t('pp_new_que3_para7')}

  * ${t('pp_new_que3_para8')}

  * ${t('pp_new_que3_para9')}

  * ${t('pp_new_que3_para10')}

* ### ${t('pp_new_que3_para11')}  

  *  ${t('pp_new_que3_para12')}

* ### ${t('pp_new_que3_para13')}

  * ${t('pp_new_que3_para14')}

  * ${t('pp_new_que3_para15')}

## 4. ${t('pp_new_que4')}

${t('pp_new_que4_para1')}

  * ${t('pp_new_que4_para2')}

  * ${replaceString(t('pp_new_que4_para3'), { Resumedone: getBrandName() })}

  * ${t('pp_new_que4_para4')}

  * ${t('pp_new_que4_para5')}

  * ${replaceString(t('pp_new_que4_para6'), { Resumedone: getBrandName() })}

${t('pp_new_que4_para7')}

## 5. ${t('pp_new_que5')}

${t('pp_new_que5_para1')}

## 6. ${t('pp_new_que6')}

${t('pp_new_que6_para1')}

${t('pp_new_que6_para2')} 

* **${t('pp_new_que6_para3')}** ${t('pp_new_que6_para4')}

  * ${t('pp_new_que6_para5')}

  * ${t('pp_new_que6_para6')} 

* **${t('pp_new_que6_para7')}** ${t('pp_new_que6_para8')}

* **${t('pp_new_que6_para9')}** ${t('pp_new_que6_para10')}

${t('pp_new_que6_para11')}

## 7. ${t('pp_new_que7')}

${t('pp_new_que7_para1')}

## 8. ${t('pp_new_que8')}

${t('pp_new_que8_para1')}

  * ${t('pp_new_que8_para2')}

  * ${t('pp_new_que8_para3')}

  * ${t('pp_new_que8_para4')}

  * ${t('pp_new_que8_para5')}

  * ${t('pp_new_que8_para6')}

  * ${t('pp_new_que8_para7')}

  * ${t('pp_new_que8_para8')}

  * ${t('pp_new_que8_para9')}

${t('pp_new_que8_para10')}

${t('pp_new_que8_para11')}

## 9. ${t('pp_new_que9')}

${t('pp_new_que9_para1')}

${replaceString(t('pp_new_que9_para2'), {
  ['/Cookie policy']: getRoute('/cookie-policy', locale, false, host, isBlogDomain(host)),
})}

## 10. ${t('pp_new_que10')}

${getString()}

## 11. ${t('pp_new_que11')}

${t('pp_new_que11_para1')}

`;
};

const getNewPrivacyPolicy = (t, locale, host, getBrandName, getContactEmail) => {
  const getPolicyTitleText = (host) => {
    let replacedValue = {
      COMPANY_NAME: getCompanyNameByHost(host),
    };
    return replaceString(t('pp_new_desc1'), replacedValue);
  };

  const getCountryOfCompany = () => {
    const country = getCompanyCountryByHost(host);
    let replacedValue = '';
    let countryName = '';

    switch (country) {
      case 'Ireland':
        countryName = 'Ireland';
        replacedValue = 'Data Protection Commission (DPC)';
        break;
      case 'United Kingdom':
        countryName = 'United Kingdom';
        replacedValue = "Information Commissioner's Office (ICO)";
        break;
      case 'Cyprus':
        countryName = 'Cyprus';
        replacedValue = 'Office of the Commissioner for Personal Data Protection';
        break;
      case 'England':
        countryName = 'England';
        replacedValue = 'ICO';
        break;
      case 'India':
        countryName = 'India';
        replacedValue = 'Data Protection Board of India (DPBI)';
        break;
      default:
        countryName = 'France';
        replacedValue = 'CNIL';
        break;
    }

    return replaceString(t('pp_new2_que8_para2'), { CNIL: replacedValue, Country: countryName });
  };

  const bulletsRow1 = [
    t('pp_new2_table_head2_row1_para1'),
    t('pp_new2_table_head2_row1_para2'),
    t('pp_new2_table_head2_row1_para3'),
    t('pp_new2_table_head2_row1_para4'),
    t('pp_new2_table_head2_row1_para5'),
  ];

  const formattedBullets1 = bulletsRow1.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow2 = [
    t('pp_new2_table_head2_row2_para1'),
    t('pp_new2_table_head2_row2_para2'),
    t('pp_new2_table_head2_row2_para3'),
    t('pp_new2_table_head2_row2_para4'),
  ];

  const formattedBullets2 = bulletsRow2.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow3 = [
    t('pp_new2_table_head2_row3_para1'),
    t('pp_new2_table_head2_row3_para2'),
    t('pp_new2_table_head2_row3_para3'),
    t('pp_new2_table_head2_row3_para4'),
    t('pp_new2_table_head2_row3_para5'),
  ];

  const formattedBullets3 = bulletsRow3.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow4 = [
    t('pp_new2_table_head2_row4_para1'),
    t('pp_new2_table_head2_row4_para2'),
    t('pp_new2_table_head2_row4_para3'),
  ];

  const formattedBullets4 = bulletsRow4.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow5 = [t('pp_new2_table_head2_row5_para1'), t('pp_new2_table_head2_row5_para2')];

  const formattedBullets5 = bulletsRow5.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow6 = [t('pp_new2_table_head2_row6_para1'), t('pp_new2_table_head2_row6_para2')];

  const formattedBullets6 = bulletsRow6.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow7 = [t('pp_new2_table_head2_row7_para1'), t('pp_new2_table_head2_row7_para2')];

  const formattedBullets7 = bulletsRow7.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow8 = [t('pp_new2_table_head2_row8')];

  const formattedBullets8 = bulletsRow8.map((item) => `• ${item}`).join(' ');

  const bulletsRow9 = [
    t('pp_new2_table2_head2_row4_para1'),
    t('pp_new2_table2_head2_row4_para2'),
    t('pp_new2_table2_head2_row4_para3'),
    t('pp_new2_table2_head2_row4_para4'),
    t('pp_new2_table2_head2_row4_para5'),
  ];

  const formattedBullets9 = bulletsRow9.map((item) => `• ${item}`).join('<br/>');

  const bulletsRow10 = [
    t('pp_new2_table2_head2_row4_para7'),
    t('pp_new2_table2_head2_row4_para8'),
    t('pp_new2_table2_head2_row4_para9'),
  ];

  const formattedBullets10 = bulletsRow10.map((item) => `• ${item}`).join('<br/>');

  return `
# ${t('privacy_policy')}

#### ${t('pp_new2_date')}

${getPolicyTitleText(host)}

${replaceString(t('pp_new2_heading1'), {
  ResumeDone: getBrandName(),
})}

${t('pp_new2_heading2')}

* ${replaceString(t('pp_new2_heading2_subhead1'), {
    site_url: getBrandName(),
    ['/site_url']: getSiteFromDomain(host),
  })}

* ${t('pp_new2_heading2_subhead2')}

${t('pp_new2_heading3')}

## 1. ${t('pp_new2_que1')}

${replaceString(t('pp_new2_que1_para1'), {
  Resumedone: getBrandName(),
  COMPANY_NAME: getCompanyNameByHost(host),
  REGISTRATION_NUMBER: getCompanyNumber(host),
  ADDRESS: getLandingAddress(host),
  COMPANY_LAW: getCompanyCountryAdjective(host),
  REGISTRATION_OFFICE: getCompanyRegistrationOffice(host),
})}

#### [2 .] ${t('pp_new2_que2')}

${replaceString(t('pp_new2_que2_para1'), {
  ResumeDone: getBrandName(),
})}

${t('pp_new2_que2_para2')}

| ${t('pp_new2_table_head1')}  | ${t('pp_new2_table_head2')}  | ${t('pp_new2_table_head3')}| ${t('pp_new2_table_head4')} |
|---------------------|-------------------------------|---------------------------------------------------------------|--------------------------------|
|1. ${t('pp_new2_table_row1')} |   |   |    |                                                                                                                                                                                                                                       
| ${t('pp_new2_table_head1_row1')} | ${formattedBullets1}  | ${t('pp_new2_table_head3_row1')} | ${t('pp_new2_table_head4_row1')}  |
| ${t('pp_new2_table_head1_row2')} | ${formattedBullets2}  | ${replaceString(t('pp_new2_table_head3_row2'), { ResumeDone: getBrandName() })}| ${t('pp_new2_table_head4_row2')}  |
| ${t('pp_new2_table_head1_row3')} | ${formattedBullets3}  | ${replaceString(t('pp_new2_table_head3_row3'), { ResumeDone: getBrandName() })} | ${t('pp_new2_table_head4_row3')}  |
|2.[ 1.] ${t('pp_new2_table_row4')} |         
| ${t('pp_new2_table_head1_row4')} | ${formattedBullets4} | ${t('pp_new2_table_head3_row4')} | ${t('pp_new2_table_head4_row4')}   |
| ${t('pp_new2_table_head1_row5')} | ${formattedBullets5} |  ${replaceString(t('pp_new2_table_head3_row5'), { ResumeDone: getBrandName() })} | ${t('pp_new2_table_head4_row5')}   | 
|3. ${t('pp_new2_table_row6')}  |     
| ${t('pp_new2_table_head1_row6')} | ${formattedBullets6} | ${replaceString(t('pp_new2_table_head3_row6'), { ResumeDone: getBrandName() })} | ${t('pp_new2_table_head4_row6')}   |
| ${t('pp_new2_table_head1_row7')} | ${formattedBullets7} | ${t('pp_new2_table_head3_row7')} | ${t('pp_new2_table_head4_row7')}   |    
| ${t('pp_new2_table_head1_row8')} | ${formattedBullets8} | ${t('pp_new2_table_head3_row8')} | ${t('pp_new2_table_head4_row8')}   |  

${t('pp_new2_que2_para3')}

${replaceString(t('pp_new2_que2_para4'), {
  ResumeDone: getBrandName(),
})}


#### [4] ${t('pp_new2_que4')}

${t('pp_new2_que4_para1')}

* ${t('pp_new2_que4_para2')}

* ${replaceString(t('pp_new2_que4_para3'), { ResumeDone: getBrandName() })}

* ${t('pp_new2_que4_para4')}

* ${t('pp_new2_que4_para5')}

* ${replaceString(t('pp_new2_que4_para6'), { ResumeDone: getBrandName() })}

${t('pp_new2_que4_para7')}

#### 2.[5.] ${t('pp_new2_que5')}

${t('pp_new2_que5_para1')}

#### 3.[6.] ${t('pp_new2_que6')}

${t('pp_new2_que6_para1')}

${t('pp_new2_que6_para2')}

* ${t('pp_new2_que6_para3')}

  * ${t('pp_new2_que6_para4')}

  * ${t('pp_new2_que6_para5')}

* ${t('pp_new2_que6_para6')}

* ${t('pp_new2_que6_para7')}

${t('pp_new2_que6_para8')}

#### 4.[7.] ${t('pp_new2_que7')}

${t('pp_new2_que7_para1')}

#### 5.[8.] ${t('pp_new2_que8')}

${t('pp_new2_que8_para1')}

| ${t('pp_new2_table2_head1')}  | ${t('pp_new2_table2_head2')} |
|---------------------|-------------------------------|
| ${t('pp_new2_table2_head1_row1')} | ${t('pp_new2_table2_head2_row1')} |
| ${t('pp_new2_table2_head1_row2')} | ${t('pp_new2_table2_head2_row2')} |
| ${t('pp_new2_table2_head1_row3')} | ${t('pp_new2_table2_head2_row3')} |
| ${t('pp_new2_table2_head1_row4')} | ${t('pp_new2_table2_head2_row4')} <br/>${formattedBullets9} <br/><br/> ${t('pp_new2_table2_head2_row4_para6')} <br/> ${formattedBullets10}|
| ${t('pp_new2_table2_head1_row5')} | ${t('pp_new2_table2_head2_row5')} |
| ${t('pp_new2_table2_head1_row6')} | ${t('pp_new2_table2_head2_row6')} |
| ${t('pp_new2_table2_head1_row7')} | ${t('pp_new2_table2_head2_row7')} |

${getCountryOfCompany()}

${t('pp_new2_que8_para3')}

#### 6.[9.] ${t('pp_new2_que9')}

${t('pp_new2_que9_para1')}

${replaceString(t('pp_new2_que9_para2'), {
  ['/COOKIES_POLICY']: `${getRoute('/cookie-policy', locale, false, host, isBlogDomain(host))}`,
})}

#### 7.[10.] ${t('pp_new2_que10')}

${replaceString(t('pp_new2_que10_para1'), {
  CONTACT_EMAIL: `[${getContactEmail(host)}](mailto:${getContactEmail(host)})`,
})}


#### 8.[11.] ${t('pp_new2_que11')}

${t('pp_new2_que11_para1')}

`;
};

@withTracking
@withDomain
@withIntl
@withResponsiveContext
class PrivacyPolicyPage extends Component {
  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('privacy_view');
  }

  render() {
    const { t, locale, host, getBrandName, getContactEmail } = this.props;
    return (
      <React.Fragment>
        {host === 'cvdeboss' && (
          <Head>
            <title>Politique de confidentialité - Cvdeboss.com</title>
          </Head>
        )}
        <Head>
          <meta name="robots" content="nofollow, noindex" />
        </Head>
        {host === 'modeles-cv' && (
          <Head>
            <title>Politique de confidentialité - modeles-cv.fr</title>
          </Head>
        )}
        <StyledMarkDown
          children={getNewPrivacyPolicy(t, locale, host, getBrandName, getContactEmail)}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
        />
      </React.Fragment>
    );
  }
}

const StyledMarkDown = styled(MarkDown)`
  padding: 50px;

  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}

  h1 {
    text-align: center;
  }

  p {
    font-size: 20px;
  }

  h4:first-of-type {
    text-align: right;
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 20px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
  }

  tr td:empty {
    border: none !important;
    padding: 0 !important;
    background-color: inherit;
  }

  tr td:not(:empty):first-child {
    border-right: none;
  }

  tr td:empty:last-child {
    border-right: 1px solid #ddd !important;
  }

  th,
  td:not(:empty) {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: left;
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
`;

export default PrivacyPolicyPage;

PrivacyPolicyPage.propTypes = {
  trackEvent: PropTypes.func,
  t: PropTypes.func,
  locale: PropTypes.string,
  host: PropTypes.string,
  getBrandName: PropTypes.func,
  getContactEmail: PropTypes.func,
};
